import { observable, toJS, makeObservable, action } from "mobx";
import { extractErrorMessage, copyTextToClipboard } from "../../../utils/helpers";
import { CrudCommon } from "../_common/CrudCommon";
import { AppStore } from "~/stores/AppStore";

import _ from "lodash";

import { translateKey as t } from "../../../locales/translation";

export class PartnersController {
  public store: AppStore;
  public partner: any = null;
  public partners: Array<any> = [];

  public formRef: any;
  public crudCommon: CrudCommon;
  public formMode = "create";
  public entitySnapshot = "";
  public fields: Array<any> = [
    { Header: t("lblActive", "Active"), id: "active", type: "boolean" },
    { Header: t("lblClientRef", "Client Ref"), id: "clientRef" },
    { Header: t("lblName", "Name"), id: "name" },
    { Header: t("lblDBName", "DB Name"), id: "dbName" },
  ];

  public mainDataHasLoaded = false;
  public additionalColumnsAreVisible = false;
  public isReadOnly = false;
  public isFormModalVisible = false;
  public saveButtonLoading = false;
  public tableInstance: any = null;
  public tableIsReadOnly = true;
  public deleteModal: any = {
    visible: false,
    description: "",
    verificationField: "",
    onConfirmDelete: () => null,
  };

  public modifiedRows: any = [];
  public multiSelectKeyPressed = false;
  public shiftKeyPressed = false;
  public clickedRow: any = {};

  public modalOptionsLocalStorageKey = "partnersFormModalOptions";
  public formFieldsSize: "small" | "middle" | "large" = "middle";
  public formModalSize: "small" | "default" | "large" = "default";
  public showNonMandatoryFields = true;
  public showFieldsIcon = false;
  public prerequisitesLoaded = true;

  constructor(store) {
    this.store = store;
    this.crudCommon = new CrudCommon(this, "partners");

    makeObservable(this, {
      partners: observable,
      partner: observable,
      formRef: observable,
      crudCommon: observable,
      formMode: observable,
      entitySnapshot: observable,
      fields: observable,
      mainDataHasLoaded: observable,
      additionalColumnsAreVisible: observable,
      isReadOnly: observable,
      isFormModalVisible: observable,
      saveButtonLoading: observable,
      tableInstance: observable,
      tableIsReadOnly: observable,
      modifiedRows: observable,
      multiSelectKeyPressed: observable,
      shiftKeyPressed: observable,
      clickedRow: observable,
      deleteModal: observable,
      modalOptionsLocalStorageKey: observable,
      formFieldsSize: observable,
      formModalSize: observable,
      showNonMandatoryFields: observable,
      showFieldsIcon: observable,
      prerequisitesLoaded: observable,

      setPartners: action,
      setAdditionalColumnsAreVisible: action,
      setSaveButtonLoading: action,
      setFormMode: action,
      setEntitySnapshot: action,
      setPartner: action,
      setMainDataHasLoaded: action,
      setIsFormModalVisible: action,
      setEntity: action,
      setTableInstance: action,
    });

    this.isReadOnly = this.store.isRouteReadOnly(window.location.pathname);

    this.setFormModalOptions();
  }

  public setEntity(entity: string, value: any) {
    this[entity] = value;
  }

  public setPartners(value: Array<any>) {
    this.partners = value;
  }

  public setPartner(value: any, key: string = null) {
    if (key === null) {
      this.partner = value;
    } else {
      this.partner[key] = value;
    }
  }

  public setSaveButtonLoading(value: boolean) {
    this.saveButtonLoading = value;
  }

  public setAdditionalColumnsAreVisible(value: boolean) {
    this.additionalColumnsAreVisible = value;
  }

  public setIsFormModalVisible(value: boolean) {
    this.isFormModalVisible = value;
  }

  public setTableInstance(value: any) {
    this.tableInstance = value;
  }

  public setMainDataHasLoaded(value: any) {
    this.mainDataHasLoaded = value;
  }

  public setFormRef(value: any) {
    this.formRef = value;
  }

  public setEntitySnapshot(value: any) {
    this.entitySnapshot = value;
  }

  public setFormMode(value: any) {
    this.formMode = value;
  }

  public async loadDependencies() {
    await this.loadPartners();
    this.setMainDataHasLoaded(true);
  }

  private async loadPartners() {
    try {
      const partners = await this.store.getData("partners");
      if (partners) {
        this.setPartners(partners);
      }
      this.setPartner(null);
    } catch (err) {
      this.store.displayNotification(t("lblFailedToLoadPartners", "Failed to Load Partners"), "error", true, extractErrorMessage(err));
    }
  }

  public openEditForm = (partner: any) => {
    this.setFormMode("edit");
    this.partner = toJS(partner);
    this.setEntitySnapshot(JSON.stringify(this.partner));
    this.setIsFormModalVisible(true);
  };

  public saveFormPartner = async () => {
    this.setSaveButtonLoading(true);

    if (this.partner.name) {
      this.setPartner(this.partner.name.trim(), "name");
    }
    if (this.partner.clientRef) {
      this.setPartner(this.partner.clientRef.trim(), "clientRef");
    }
    if (this.partner.dbName) {
      this.setPartner(this.partner.dbName.trim(), "dbName");
    }

    if (this.formMode === "create") {
      await this.store
        .postData("partners", toJS(this.partner))
        .then((response) => {
          this.partners.push(observable(response));
          this.reorderTable();

          this.setIsFormModalVisible(false);
          this.tableInstance.handleRowSelection(response);

          this.store.displayNotification(
            t("lblPartnerCreatedSuccessfully", "Partner Created Successfully"),
            "success",
            true,
            t("lblName", "Name") + ": " + response.name
          );
        })
        .catch((err) => {
          this.store.displayNotification(
            t("lblFailedToCreatePartner", "Failed to Create Partner"),
            "error",
            true,
            extractErrorMessage(err)
          );
        });
    }

    if (this.formMode === "edit") {
      const payload: any = toJS(this.partner);

      await this.store
        .postData("partners/" + this.partner._id, payload)
        .then((updatedPartner) => {
          const foundIndex: number = this.partners.findIndex((p) => p._id === this.partner._id);
          this.partners[foundIndex] = updatedPartner;

          this.reorderTable();

          this.setIsFormModalVisible(false);
          this.tableInstance.handleRowSelection(updatedPartner, false, true);

          this.store.displayNotification(
            t("lblPartnerUpdatedSuccessfully", "Partner Updated Successfully"),
            "success",
            true,
            t("lblName", "Name") + ": " + updatedPartner.name
          );
        })
        .catch((err) => {
          this.store.displayNotification(
            t("lblFailedToEditPartner", "Failed to Edit Partner"),
            "error",
            true,
            extractErrorMessage(err)
          );
        });
    }

    this.setSaveButtonLoading(false);
  };

  private setFormModalOptions = () => {
    if (localStorage.getItem(this.modalOptionsLocalStorageKey)) {
      const localStorageObj: any = JSON.parse(localStorage.getItem(this.modalOptionsLocalStorageKey));

      this.formModalSize = localStorageObj.formModalSize.currentlySet;
      this.formFieldsSize = localStorageObj.formFieldsSize.currentlySet;
      this.showNonMandatoryFields = localStorageObj.showNonMandatoryFields.currentlySet;
      this.showFieldsIcon = localStorageObj.showFieldsIcon.currentlySet;
    } else {
      const defaults = {
        formModalSize: { default: "default", currentlySet: "default" },
        formFieldsSize: { default: "default", currentlySet: "default" },
        showNonMandatoryFields: { default: true, currentlySet: true },
        showFieldsIcon: { default: false, currentlySet: false },
      };
      localStorage.setItem(this.modalOptionsLocalStorageKey, JSON.stringify(defaults));
    }
  };

  private reorderTable = () => {
    this.partners = _.sortBy(this.partners, [(p) => (p.name || "").toLowerCase()]);
  };

  public handleContextMenuEdit = (row: any) => {
    this.tableInstance.handleRowClick(row);
    this.openEditForm(row);
  };

  // Wraps crudCommon.handleRowClick so we can keep the delete-confirmation field
  // synced with whichever partner is currently selected.
  public handleRowClick = (row: any) => {
    this.crudCommon.handleRowClick(row);
    this.deleteModal.verificationField = (row && row.clientRef) || "";
  };

  public openDeleteModal = (row: any) => {
    if (!row) return;
    this.tableInstance && this.tableInstance.handleRowClick && this.tableInstance.handleRowClick(row);
    this.deleteModal.verificationField = row.clientRef || "";
    this.deleteModal.description = t(
      "lblPartnerDeletionWarning",
      "This will permanently drop the '{1}' database and remove the partner, its client apps, and all cronjobs from core. This cannot be undone.",
      [row.dbName || row.clientRef]
    );
    this.deleteModal.onConfirmDelete = () => this.deletePartner(row);
    this.deleteModal.visible = true;
  };

  public deletePartner = async (partner?: any) => {
    const target = partner || this.clickedRow;
    if (!target || !target._id) {
      this.store.displayNotification(
        t("lblFailedToDeletePartner", "Failed to Delete Partner"),
        "error",
        true,
        t("lblNoPartnerSelected", "No partner selected")
      );
      return;
    }

    try {
      const body = { clientRef: target.clientRef };
      const response: any = await this.store.deleteData("partners/" + target._id, "main", false, body);
      const result = response && response.data ? response.data : response;

      this.partners = this.partners.filter((p) => p._id !== target._id);
      if (this.tableInstance && this.tableInstance.resetSelectedRow) this.tableInstance.resetSelectedRow();
      if (this.tableInstance && this.tableInstance.resetMultiselectedRows) this.tableInstance.resetMultiselectedRows();
      this.clickedRow = {};
      this.deleteModal.verificationField = "";

      const droppedDb = result && result.droppedDb;
      const deletedClientApps = result && result.deletedClientApps;
      const detail =
        (target.name ? `${t("lblName", "Name")}: ${target.name}. ` : "") +
        (droppedDb ? t("lblDroppedDb", "Dropped DB") + `: ${droppedDb}. ` : "") +
        (typeof deletedClientApps === "number" ? `${t("lblClientApps", "Client Apps")}: ${deletedClientApps}` : "");

      this.store.displayNotification(
        t("lblPartnerDeletedSuccessfully", "Partner Deleted Successfully"),
        "success",
        true,
        detail.trim()
      );
    } catch (err) {
      this.store.displayNotification(
        t("lblFailedToDeletePartner", "Failed to Delete Partner"),
        "error",
        true,
        extractErrorMessage(err)
      );
    }
  };

  public handleContextMenuDelete = (row: any) => {
    this.openDeleteModal(row);
  };

  public copyText = (value: string) => {
    copyTextToClipboard(value);
    this.store.displayNotification(t("lblCopiedToClipboard", "Copied to Clipboard"), "info", false);
  };

  public setNewPartner() {
    this.setPartner({
      clientRef: "",
      name: "",
      dbName: "",
      active: true,
    });
    this.setEntitySnapshot(JSON.stringify(this.partner));
  }
}
